import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowLeft01Icon } from '@hugeicons/core-free-icons';
import { parseFramerReviewUrl } from '@overlayqa/shared';
import { ModalAction } from '@overlayqa/shared/ui';
import { useProjects } from '../hooks/useProjects';
import { SelectDropdown } from '../components/shared/SelectDropdown';
import { LoadError } from '../components/shared/IssuesLoadError';
import { BrandTile } from '../components/settings/integrationLogos';
import { ClientReviewCreateModal } from '../components/projects/ClientReviewCreateModal';
import { ProjectsCreateModal } from './projects/ProjectsCreateModal';
import { useAppSession } from '../hooks/useAppSession';
import { capture } from '../lib/analytics';

const EDITABLE_ROLES = ['owner', 'collaborator', 'team'];

/** Framer provides page context; account permissions and creation stay in OverlayQA. */
export function FramerReviewPage() {
  const [params] = useSearchParams();
  const pageUrl = parseFramerReviewUrl(params.get('url'));
  const query = useProjects(undefined, { allTeams: true, includePreviews: false, includeCounts: false });
  const [projectId, setProjectId] = useState('');
  const [creating, setCreating] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);
  const { data: session } = useAppSession();
  const projects = (query.data ?? []).filter(project => !project.archivedAt && !project.isDemo &&
    EDITABLE_ROLES.includes(project.role ?? ''));
  // One editable project is unambiguous; several need an explicit choice
  // (the workspace rule in McpConnectPage).
  const selected = !pageUrl ? undefined
    : projects.find(project => project.id === projectId) ?? (projects.length === 1 ? projects[0] : undefined);
  const ready = !query.isLoading && !query.isError;
  const noProjects = ready && projects.length === 0;
  // Layout renders this page only once the session has loaded, and bootstrap
  // always returns an active team (auth-bootstrap.service.ts throws otherwise).
  const teamId = session?.activeTeamId;
  // Host on its own line, path truncated under it: a 2,000-character URL (the
  // plugin's cap) otherwise wraps into a column taller than the screen.
  const page = pageUrl ? new URL(pageUrl) : null;
  const pagePath = page ? `${page.pathname}${page.search}${page.hash}` : '';
  // Projects come from every team; a team name tells same-named projects apart.
  const teamNames = new Map((session?.teams ?? []).map(team => [team.id, team.name]));
  const projectLabel = (project: { name: string; teamId?: string | null }) =>
    teamNames.size > 1 && project.teamId && teamNames.has(project.teamId)
      ? `${project.name} · ${teamNames.get(project.teamId)}` : project.name;

  const createProjectButton = (primary: boolean) => teamId && (
    <button type="button" className={primary ? 'btn-primary' : 'btn-ghost btn-sm -my-1 -mr-2'} onClick={() => setCreatingProject(true)}>
      <ModalAction kind="add">Create a project</ModalAction>
    </button>
  );

  return (
    <section id="framer-review" className="page-content">
      <div className="page-content__inner">
        <header className="mb-6">
          <h1 className="hero-title">Review your Framer page</h1>
          <p className="mt-2 text-sm text-gray-600 [text-wrap:pretty]">Choose where to collect feedback, then create a link for your reviewers.</p>
        </header>

        <div className="card-sm">
          {!pageUrl ? (
            <div role="alert">
              <h2 className="text-sm font-semibold text-gray-900">Page URL missing or invalid</h2>
              <p className="mt-1 text-sm text-gray-600 [text-wrap:pretty]">Return to the OverlayQA plugin in Framer and choose a published HTTPS page.</p>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <h2 id="framer-page-label" className="field-label">Published page</h2>
                <div className="flex min-h-10 items-center gap-3 rounded-md border border-gray-200 bg-gray-50 py-2 pl-2 pr-2" aria-labelledby="framer-page-label">
                  <BrandTile mark="framer" />
                  <p className="min-w-0 flex-1 text-sm" title={pageUrl}>
                    <span className="block truncate font-medium text-gray-900">{page?.host}</span>
                    {pagePath !== '/' && <span className="block truncate text-xs text-gray-500">{pagePath}</span>}
                  </p>
                  <a href={pageUrl} target="_blank" rel="noopener noreferrer" className="btn-ghost btn-sm shrink-0"
                    aria-label="Open the published page in a new tab"
                    onClick={() => capture('integration_link_clicked', { provider: 'framer', placement: 'framer_handoff', target: 'published_page' })}>
                    <ModalAction kind="open">Open</ModalAction>
                  </a>
                </div>
                <p className="field-hint mt-2">The review uses the published page. Publish any editor changes first.</p>
              </div>

              {query.isError ? (
                <LoadError resource="your projects" error={query.error ?? new Error('Unknown error')} compact
                  onRetry={() => query.refetch()} retrying={query.isFetching} />
              ) : noProjects ? (
                <div>
                  <h2 className="field-label">Project</h2>
                  <p className="rounded-md border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-600">You need a project you can edit to collect feedback.</p>
                </div>
              ) : (
                <div>
                  <div className="mb-1 flex items-center justify-between gap-3">
                    <label htmlFor="framer-project" className="field-label mb-0">Project</label>
                    {ready && createProjectButton(false)}
                  </div>
                  <SelectDropdown id="framer-project" value={selected?.id ?? ''} size="lg" className="w-full"
                    disabled={!ready} placeholder={ready ? 'Choose a project' : 'Loading your projects...'}
                    options={projects.map(project => ({ value: project.id, label: projectLabel(project) }))}
                    onChange={setProjectId} />
                  {query.isLoading && <span role="status" className="sr-only">Loading your projects...</span>}
                </div>
              )}
            </div>
          )}

          <footer className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 py-4"
            style={{ marginInline: 'calc(-1 * var(--oq-spacing-card))', marginBottom: 'calc(-1 * var(--oq-spacing-card))', paddingInline: 'var(--oq-spacing-card)' }}>
            <Link to={selected ? `/projects/${selected.id}/issues` : '/projects'} className="btn-ghost -ml-2">
              <HugeiconsIcon icon={ArrowLeft01Icon} size={16} strokeWidth={2} color="currentColor" aria-hidden="true" />
              <span className="text-cap-trim">{selected ? 'Back to project' : 'Back to projects'}</span>
            </Link>
            {pageUrl && !query.isError && (noProjects ? createProjectButton(true) : (
              <button type="button" className="btn-primary" disabled={!selected} onClick={() => setCreating(true)}>
                <ModalAction kind="link">Share client link</ModalAction>
              </button>
            ))}
          </footer>
        </div>
      </div>

      {creating && selected && pageUrl && <ClientReviewCreateModal projectId={selected.id} initialUrl={pageUrl}
        onClose={() => setCreating(false)} guestDefaults={{
          welcomeEnabled: selected.clientReviewWelcomeEnabled === true,
          welcomeNote: selected.clientReviewWelcomeNote ?? '',
          privateComments: selected.clientReviewPrivateComments === true,
          allowResponsiveViews: selected.clientReviewAllowResponsive !== false,
        }} />}
      {creatingProject && teamId && <ProjectsCreateModal activeTeamId={teamId} onCreated={setProjectId}
        onClose={() => { setCreatingProject(false); void query.refetch(); }} />}
    </section>
  );
}
